/**
 * PlaybookDetailPage
 *
 * Shows a single agent's configuration, methodology, and run history.
 * Title and goal are editable in place; archive/delete live in the kebab menu.
 *
 * Follows DataSyncJobPage patterns: route param, polling while running,
 * download link for completed runs.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNew';
import EditRounded from '@mui/icons-material/EditRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import UnarchiveRounded from '@mui/icons-material/UnarchiveRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import ChatOutlinedRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import EmailRounded from '@mui/icons-material/EmailRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/dialog';
import {
  getPlaybook,
  listPlaybookRuns,
  triggerPlaybookRun,
  triggerManualPlaybookRun,
  updatePlaybook,
  deletePlaybook,
  getDocumentDownloadUrl,
  getTemplateDownloadUrl,
  sendRunEmail,
  uploadPlaybookTemplate,
  deletePlaybookTemplate,
} from '../../services/playbooksService';
import { useCopilotStore } from '../copilot/stores/useCopilotStore';
import type { Playbook, PlaybookRun, PlaybookRunStatus, RunArtifact, UpdatePlaybookRequest } from '../../types/playbooks';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { formatRelativeTime } from '../../utils/date';
import SchedulesManager from './components/SchedulesManager';

async function downloadAuthenticatedFile(url: string, fallbackFilename: string): Promise<void> {
  const token = localStorage.getItem('klaris_jwt');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/i);
  const filename = match?.[1] || fallbackFilename;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

// ─── Status chip ─────────────────────────────────────────────────────────────

function RunStatusChip({ status }: { status: PlaybookRunStatus }) {
  const map: Record<PlaybookRunStatus, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
    completed: { label: 'Completed', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
    running: { label: 'Running', color: 'info' },
    waiting_on_user: { label: 'Awaiting input', color: 'warning' },
    pending: { label: 'Pending', color: 'warning' },
  };
  const { label, color } = map[status] ?? { label: status, color: 'default' };
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600 }}
    />
  );
}

// ─── Inline edit (click-to-edit for title + goal) ────────────────────────────

interface InlineEditProps {
  value: string;
  onSave: (next: string) => Promise<void>;
  ariaLabel: string;
  multiline?: boolean;
  maxLength?: number;
  editInputSx?: object;
  children: React.ReactNode;
}

function InlineEdit({ value, onSave, ariaLabel, multiline = false, maxLength, editInputSx, children }: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === value) {
      setDraft(value);
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      setDraft(value); // parent surfaces the error toast
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    if (multiline) {
      return (
        <Stack spacing={1}>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
            multiline
            minRows={3}
            maxRows={12}
            fullWidth
            autoFocus
            disabled={saving}
            size="small"
            inputProps={{ maxLength, 'aria-label': ariaLabel }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={commit}
              disabled={saving || !draft.trim()}
              startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <CheckRounded sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none' }}
            >
              Save
            </Button>
            <Button size="small" variant="text" onClick={cancel} disabled={saving} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      );
    }
    return (
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') cancel();
        }}
        onBlur={commit}
        autoFocus
        fullWidth
        disabled={saving}
        variant="standard"
        inputProps={{ maxLength, 'aria-label': ariaLabel }}
        InputProps={{ sx: editInputSx }}
      />
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Edit ${ariaLabel}`}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); } }}
      sx={{
        display: 'flex',
        alignItems: multiline ? 'flex-start' : 'center',
        gap: 0.75,
        cursor: 'text',
        borderRadius: 1,
        mx: -0.5,
        px: 0.5,
        transition: 'background-color 0.12s',
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover .inline-edit-pencil, &:focus-visible .inline-edit-pencil': { opacity: 1 },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
      <EditRounded
        className="inline-edit-pencil"
        sx={{ fontSize: 14, color: 'text.disabled', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0, mt: multiline ? '3px' : 0 }}
      />
    </Box>
  );
}

// ─── "What this agent does" (methodology brief) ──────────────────────────────

function briefArray(brief: Record<string, unknown> | null, key: string): any[] {
  const v = brief ? (brief as Record<string, unknown>)[key] : null;
  return Array.isArray(v) ? v : [];
}

function BriefSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Interprets the brief's ``period_strategy`` into a human-readable reporting
 * window descriptor. Mirrors the choices in the Save-as-Agent modal:
 *  - ""                    → decided at run time (the agent asks)
 *  - "Fixed window: …"     → a locked window that never moves
 *  - anything else         → a rolling relative rule, recomputed each run
 */
function describePeriod(strategy: unknown): { label: string; value: string; helper: string; summary: string } {
  const s = typeof strategy === 'string' ? strategy.trim() : '';
  if (!s) {
    return {
      label: 'At run time',
      value: 'Chosen on each run',
      helper: 'The agent asks which period to report on every time it runs.',
      summary: 'period set at run time',
    };
  }
  const fixed = s.match(/^fixed window:\s*(.+)$/i);
  if (fixed) {
    return {
      label: 'Fixed',
      value: `Always ${fixed[1]}`,
      helper: 'The window never moves between runs.',
      summary: 'fixed period',
    };
  }
  return {
    label: 'Rolling',
    value: `The ${s}`,
    helper: 'Recomputed on every run so it always uses the latest window.',
    summary: 'rolling period',
  };
}

function PeriodSection({ period }: { period: ReturnType<typeof describePeriod> }) {
  return (
    <BriefSection label="Reporting period">
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip label={period.label} size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{period.value}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {period.helper}
      </Typography>
    </BriefSection>
  );
}

function AgentBriefCard({ brief }: { brief: Record<string, unknown> | null }) {
  const [open, setOpen] = useState(false);

  const dataSources = briefArray(brief, 'data_sources');
  const keyMetrics = briefArray(brief, 'key_metrics');
  const businessRules = briefArray(brief, 'business_rules');
  const outputStructure = briefArray(brief, 'output_structure');
  const period = describePeriod(brief?.period_strategy);

  const hasAny =
    dataSources.length || keyMetrics.length || businessRules.length || outputStructure.length;

  // The brief is distilled in a background task right after the agent is
  // created, so on the freshly-redirected detail page it isn't ready yet.
  // Show a clear "being prepared" state instead of an empty gap.
  if (!hasAny) {
    return (
      <Box
        sx={(t) => ({
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
          p: 2,
        })}
      >
        <Stack spacing={1.25}>
          <Typography variant="subtitle2" fontWeight={700}>
            What this agent does
          </Typography>
          <PeriodSection period={period} />
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <CircularProgress size={14} sx={{ mt: 0.25, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              We're preparing a summary of how this agent works — it'll appear here shortly. This is
              optional context; you can run the agent now.
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Concise one-line summary shown while collapsed.
  const summaryParts: string[] = [];
  summaryParts.push(period.summary);
  if (dataSources.length) summaryParts.push(`${dataSources.length} data source${dataSources.length > 1 ? 's' : ''}`);
  if (keyMetrics.length) summaryParts.push(`${keyMetrics.length} metric${keyMetrics.length > 1 ? 's' : ''}`);
  if (businessRules.length) summaryParts.push(`${businessRules.length} rule${businessRules.length > 1 ? 's' : ''}`);
  if (outputStructure.length) summaryParts.push(`${outputStructure.length} section${outputStructure.length > 1 ? 's' : ''}`);
  const summary = summaryParts.join(' · ');

  return (
    <Box
      sx={(t) => ({
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
        overflow: 'hidden',
      })}
    >
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{ width: '100%', display: 'block', textAlign: 'left', p: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ flexShrink: 0 }}>
            What this agent does
          </Typography>
          {!open && summary && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {summary}
            </Typography>
          )}
          <Box sx={{ flexGrow: open ? 1 : 0 }} />
          <ExpandMoreRounded
            sx={{ fontSize: 20, color: 'text.secondary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}
          />
        </Stack>
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
      <Stack spacing={1.75} sx={{ px: 2, pb: 2 }}>
        <PeriodSection period={period} />

        {dataSources.length > 0 && (
          <BriefSection label="Data sources">
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {dataSources.map((ds, i) => {
                const name = (ds && ((ds as any).name || (ds as any).connector_type)) || 'Source';
                return <Chip key={i} label={String(name)} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />;
              })}
            </Stack>
          </BriefSection>
        )}

        {keyMetrics.length > 0 && (
          <BriefSection label="Key metrics">
            <Stack component="ul" spacing={0.4} sx={{ m: 0, pl: 2.25 }}>
              {keyMetrics.map((m, i) => {
                const name = (m as any)?.name;
                const def = (m as any)?.definition;
                return (
                  <Box component="li" key={i} sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
                    {name && <Box component="span" sx={{ fontWeight: 600 }}>{String(name)}</Box>}
                    {name && def ? ' — ' : ''}
                    {def && <Box component="span" sx={{ color: 'text.secondary' }}>{String(def)}</Box>}
                  </Box>
                );
              })}
            </Stack>
          </BriefSection>
        )}

        {businessRules.length > 0 && (
          <BriefSection label="Business rules">
            <Stack component="ul" spacing={0.4} sx={{ m: 0, pl: 2.25 }}>
              {businessRules.map((r, i) => (
                <Box component="li" key={i} sx={{ fontSize: '0.8125rem', lineHeight: 1.5, color: 'text.secondary' }}>
                  {String(r)}
                </Box>
              ))}
            </Stack>
          </BriefSection>
        )}

        {outputStructure.length > 0 && (
          <BriefSection label="Report sections">
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {outputStructure.map((s, i) => (
                <Chip key={i} label={String(s)} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
              ))}
            </Stack>
          </BriefSection>
        )}
      </Stack>
      </Collapse>
    </Box>
  );
}

// ─── Output card (Word report + optional filled template) ──────────────────────

/**
 * Surfaces the two distinct, mutually-exclusive output categories:
 *  - generate:      the agent authors a free-form document from scratch.
 *  - fill_template: the agent fills the user's uploaded template, untouched
 *                   except for the placeholders.
 * The mode is always shown explicitly so it's never ambiguous which one applies.
 */
function OutputCard({
  playbook,
  onChange,
}: {
  playbook: Playbook;
  onChange: (updated: Playbook) => void;
}) {
  const { notify } = useContext(SnackbarContext);
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // A Word report is always produced; an attached Excel template is filled too.
  const isTemplate = Boolean(playbook.has_output_template);
  const mediumLabel = isTemplate ? 'Word + Excel' : 'Word';

  const manifest = playbook.output_template_manifest || null;
  const scalars = manifest?.scalars ?? [];
  const tables = manifest?.tables ?? [];
  const warnings = manifest?.warnings ?? [];

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setBusy(true);
    try {
      const updated = await uploadPlaybookTemplate(playbook.playbook_id, file);
      onChange(updated);
      setDetailsOpen(true);
      notify('Template uploaded. This agent now fills your Excel file.', 'success');
    } catch (err: any) {
      notify(err?.message || 'Failed to upload template', 'error');
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      const updated = await deletePlaybookTemplate(playbook.playbook_id);
      onChange(updated);
      notify('Template removed. Agent reverted to a generated document.', 'success');
    } catch (err: any) {
      notify(err?.message || 'Failed to remove template', 'error');
    } finally {
      setBusy(false);
    }
  };

  const onDownloadTemplate = async () => {
    if (downloadingTemplate) return;
    setDownloadingTemplate(true);
    try {
      const url = getTemplateDownloadUrl(playbook.playbook_id);
      const safeName = (playbook.name || 'agent_template').replace(/[^a-z0-9_\-]/gi, '_').toLowerCase();
      await downloadAuthenticatedFile(url, `${safeName}_template.xlsx`);
    } catch (err: any) {
      notify(err?.message || 'Failed to download template', 'error');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <Box
      sx={(t) => ({
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
        overflow: 'hidden',
      })}
    >
      <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onFile} />
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                Output
              </Typography>
              <Chip
                label={isTemplate ? 'Word report + Excel template' : 'Word report'}
                color={isTemplate ? 'success' : 'default'}
                size="small"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {isTemplate
                ? 'Each run, the agent writes a Word report and also fills your uploaded Excel template — preserving its formatting, formulas, and layout (only the placeholders are written).'
                : 'Each run, the agent writes a Word report from scratch. You can also attach an Excel template for it to fill alongside the report.'}
            </Typography>
          </Stack>
          {isTemplate ? (
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={onDownloadTemplate}
                disabled={busy || downloadingTemplate}
                startIcon={downloadingTemplate ? <CircularProgress size={14} color="inherit" /> : <DownloadRounded sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none' }}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={pickFile}
                disabled={busy || downloadingTemplate}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <UploadFileRounded sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none' }}
              >
                Replace
              </Button>
              <Button variant="text" size="small" color="error" onClick={onRemove} disabled={busy || downloadingTemplate} sx={{ textTransform: 'none' }}>
                Remove
              </Button>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={pickFile}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <UploadFileRounded sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Add an Excel template
            </Button>
          )}
        </Stack>

        {/* generate mode: explain the alternative without cluttering the card */}
        {!isTemplate && (
          <Box sx={{ mt: 1 }}>
            <ButtonBase
              onClick={() => setHelpOpen((v) => !v)}
              aria-expanded={helpOpen}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                What's a template?
              </Typography>
              <ExpandMoreRounded sx={{ fontSize: 18, color: 'text.secondary', transform: helpOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </ButtonBase>
            <Collapse in={helpOpen} unmountOnExit>
              <Box
                sx={(t) => ({ mt: 1, p: 1.5, borderRadius: 1, bgcolor: t.tokens?.surface?.overlay ?? 'rgba(0,0,0,0.03)' })}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  In addition to its Word report, the agent can also fill an Excel file you design — keeping your
                  branding, formulas, and layout. Add placeholders to the cells you want filled:
                </Typography>
                <Stack component="ul" spacing={0.4} sx={{ m: 0, pl: 2.25 }}>
                  <Box component="li" sx={{ fontSize: '0.75rem', lineHeight: 1.5, color: 'text.secondary' }}>
                    <code>{'{{ metric_name }}'}</code> — a text value
                  </Box>
                  <Box component="li" sx={{ fontSize: '0.75rem', lineHeight: 1.5, color: 'text.secondary' }}>
                    <code>{'{%xv metric_name %}'}</code> — a number (keeps the cell's number format)
                  </Box>
                  <Box component="li" sx={{ fontSize: '0.75rem', lineHeight: 1.5, color: 'text.secondary' }}>
                    <code>{'{% for row in items %}'}</code> … <code>{'{{ row.field }}'}</code> … <code>{'{% endfor %}'}</code> —
                    a dynamic table. Put the <code>for</code> and <code>endfor</code> on different rows to repeat rows
                    vertically.
                  </Box>
                </Stack>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* template mode: warnings + the placeholders it fills */}
        {isTemplate && (manifest?.warnings?.length ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
            {warnings.join(' ')}
          </Alert>
        )}

        {isTemplate && (scalars.length > 0 || tables.length > 0) && (
          <>
            <ButtonBase
              onClick={() => setDetailsOpen((v) => !v)}
              aria-expanded={detailsOpen}
              sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {detailsOpen ? 'Hide' : 'Show'} the placeholders it fills ({scalars.length} value{scalars.length === 1 ? '' : 's'}
                {tables.length ? ` · ${tables.length} table${tables.length === 1 ? '' : 's'}` : ''})
              </Typography>
              <ExpandMoreRounded sx={{ fontSize: 18, color: 'text.secondary', transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </ButtonBase>
            <Collapse in={detailsOpen} unmountOnExit>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {scalars.length > 0 && (
                  <BriefSection label="Values">
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {scalars.map((s) => (
                        <Tooltip key={s.key} title={s.sheet ? `${s.sheet}!${s.cell}` : ''} arrow disableHoverListener={!s.sheet}>
                          <Chip label={s.key} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem', fontFamily: 'monospace' }} />
                        </Tooltip>
                      ))}
                    </Stack>
                  </BriefSection>
                )}
                {tables.map((t) => (
                  <BriefSection key={t.name} label={`Table: ${t.name}${t.sheet ? ` (${t.sheet})` : ''}`}>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {(t.columns ?? []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">columns inferred from the loop body</Typography>
                      ) : (
                        (t.columns ?? []).map((c) => (
                          <Chip key={c} label={c} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
                        ))
                      )}
                    </Stack>
                  </BriefSection>
                ))}
              </Stack>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ACTIVE_STATUSES = new Set<PlaybookRunStatus>(['pending', 'running', 'waiting_on_user']);
const POLL_INTERVAL_MS = 5_000;
const DEFAULT_RUNS_PAGE_SIZE = 10;

export default function PlaybookDetailPage() {
  const { playbookId } = useParams<{ playbookId: string }>();
  const navigate = useNavigate();
  const { notify } = useContext(SnackbarContext);

  const [playbook, setPlaybook] = useState<Playbook | null>(null);
  const [runs, setRuns] = useState<PlaybookRun[]>([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsPage, setRunsPage] = useState(0);
  const [runsRowsPerPage, setRunsRowsPerPage] = useState(DEFAULT_RUNS_PAGE_SIZE);
  const [loadingPlaybook, setLoadingPlaybook] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [isRunning, setIsRunning] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadingRunId, setDownloadingRunId] = useState<string | null>(null);
  const [sendingEmailRunId, setSendingEmailRunId] = useState<string | null>(null);

  const [manualRunOpen, setManualRunOpen] = useState(false);
  const [manualRunPeriodStart, setManualRunPeriodStart] = useState('');
  const [manualRunPeriodEnd, setManualRunPeriodEnd] = useState('');
  const [manualRunSubmitting, setManualRunSubmitting] = useState(false);
  const [manualRunError, setManualRunError] = useState<string | null>(null);

  // ── Data fetchers ─────────────────────────────────────────────────────────

  const fetchPlaybook = useCallback(async () => {
    if (!playbookId) return;
    try {
      const data = await getPlaybook(playbookId);
      setPlaybook(data);
    } catch (err: any) {
      setPageError(err.message || 'Failed to load agent');
    } finally {
      setLoadingPlaybook(false);
    }
  }, [playbookId]);

  const fetchRuns = useCallback(async () => {
    if (!playbookId) return;
    try {
      const data = await listPlaybookRuns(playbookId, {
        limit: runsRowsPerPage,
        offset: runsPage * runsRowsPerPage,
      });
      setRuns(data.runs);
      setRunsTotal(data.total);
    } catch {
      /* non-critical */
    } finally {
      setLoadingRuns(false);
    }
  }, [playbookId, runsPage, runsRowsPerPage]);

  useEffect(() => { fetchPlaybook(); }, [fetchPlaybook]);

  useEffect(() => {
    setLoadingRuns(true);
    fetchRuns();
  }, [fetchRuns]);

  // Poll while any run is active
  useEffect(() => {
    const hasActive = runs.some((r) => ACTIVE_STATUSES.has(r.status));
    if (!hasActive) return;
    const t = setInterval(fetchRuns, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [runs, fetchRuns]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const saveField = useCallback(async (patch: UpdatePlaybookRequest) => {
    if (!playbook) return;
    try {
      const updated = await updatePlaybook(playbook.playbook_id, patch);
      setPlaybook(updated);
      notify('Agent updated', 'success');
    } catch (err: any) {
      notify(err.message || 'Update failed', 'error');
      throw err;
    }
  }, [playbook, notify]);

  const handleRun = async () => {
    if (!playbookId) return;
    setIsRunning(true);
    try {
      const result = await triggerPlaybookRun(playbookId);
      // Use pendingAutoSend so CopilotPage auto-fires the stream on landing
      const store = useCopilotStore.getState();
      store.resetSession();
      store.setThreadId(result.thread_id);
      store.setInputText(result.goal_message);
      store.setPendingAgentType('playbook');
      store.setPendingAutoSend(true);
      navigate(`/copilot/${result.thread_id}`);
    } catch (err: any) {
      notify(err.message || 'Failed to start run', 'error');
    } finally {
      setIsRunning(false);
    }
  };

  const handleDownloadRun = async (run: PlaybookRun, artifact?: RunArtifact) => {
    if (!playbookId || downloadingRunId) return;
    setDownloadingRunId(`${run.run_id}:${artifact?.artifact_id ?? 'default'}`);
    try {
      const url = getDocumentDownloadUrl(playbookId, run.run_id, artifact?.artifact_id);
      const safeName = (playbook?.name || 'agent_report').replace(/[^a-z0-9_\-]/gi, '_').toLowerCase();
      const fmt = artifact?.format || 'docx';
      const ext = fmt === 'xlsx' ? 'xlsx' : fmt === 'pptx' ? 'pptx' : fmt === 'pdf' ? 'pdf' : 'docx';
      await downloadAuthenticatedFile(url, `${safeName}.${ext}`);
    } catch (err: any) {
      notify(err.message || 'Download failed', 'error');
    } finally {
      setDownloadingRunId(null);
    }
  };

  const handleSendEmail = async (run: PlaybookRun) => {
    if (!playbookId) return;
    setSendingEmailRunId(run.run_id);
    try {
      // Fetch the active schedules to collect recipient emails.
      const { listPlaybookSchedules: fetchSchedules } = await import('../../services/playbooksService');
      const schedules = await fetchSchedules(playbookId);
      const activeSchedules = schedules.filter((s) => s.enabled);
      const allEmails: string[] = Array.from(
        new Set(
          activeSchedules.flatMap((s) => s.recipient_emails ?? []).filter(Boolean),
        ),
      );
      if (allEmails.length === 0) {
        notify('No recipient emails configured. Add recipients in the Schedules section.', 'warning');
        return;
      }
      const result = await sendRunEmail(playbookId, run.run_id, allEmails);
      if (result.ok) {
        notify(`Report emailed to ${allEmails.join(', ')}`, 'success');
      } else {
        notify(result.message || 'Email failed', 'error');
      }
    } catch (err: any) {
      notify(err.message || 'Failed to send email', 'error');
    } finally {
      setSendingEmailRunId(null);
    }
  };

  const handleArchiveToggle = async () => {
    if (!playbook) return;
    const nextStatus = playbook.status === 'active' ? 'archived' : 'active';
    try {
      const updated = await updatePlaybook(playbook.playbook_id, { status: nextStatus });
      setPlaybook(updated);
      notify(nextStatus === 'archived' ? 'Agent archived' : 'Agent restored', 'success');
    } catch (err: any) {
      notify(err.message || 'Update failed', 'error');
    }
    setMoreAnchor(null);
  };

  const handleDelete = async () => {
    if (!playbook) return;
    setDeleting(true);
    try {
      await deletePlaybook(playbook.playbook_id);
      notify('Agent deleted', 'info');
      navigate('/agents');
    } catch (err: any) {
      notify(err.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const openManualRunDialog = () => {
    // Default to month-to-date: 1st of this month through today.
    const now = new Date();
    const toIso = (d: Date) => d.toISOString().slice(0, 10);
    setManualRunPeriodStart(toIso(new Date(now.getFullYear(), now.getMonth(), 1)));
    setManualRunPeriodEnd(toIso(now));
    setManualRunError(null);
    setMoreAnchor(null);
    setManualRunOpen(true);
  };

  const handleManualRunSubmit = async () => {
    if (!playbookId) return;
    if (!manualRunPeriodStart || !manualRunPeriodEnd) {
      setManualRunError('Both dates are required.');
      return;
    }
    if (manualRunPeriodEnd < manualRunPeriodStart) {
      setManualRunError('Period end must be on or after period start.');
      return;
    }
    setManualRunSubmitting(true);
    setManualRunError(null);
    try {
      await triggerManualPlaybookRun(playbookId, {
        period_start: manualRunPeriodStart,
        period_end: manualRunPeriodEnd,
      });
      notify('Manual run started', 'success');
      setManualRunOpen(false);
      fetchRuns();
    } catch (err: any) {
      setManualRunError(err.message || 'Failed to start manual run');
    } finally {
      setManualRunSubmitting(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  if (loadingPlaybook) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pageError || !playbook) {
    return (
      <Box>
        <Button variant="text" startIcon={<ArrowBackRounded />} onClick={() => navigate('/agents')} sx={{ mb: 2, textTransform: 'none' }}>
          Back to Agents
        </Button>
        <Alert severity="error">{pageError || 'Agent not found'}</Alert>
      </Box>
    );
  }

  const isArchived = playbook.status === 'archived';

  return (
    <Box>
      {/* Header — back + title + metadata + actions (page title is in the breadcrumb) */}
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2, pb: 1.5, borderBottom: (t) => `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}` }}>
        <Tooltip title="Back to Agents">
          <IconButton size="small" onClick={() => navigate('/agents')} sx={{ mt: 0.25, flexShrink: 0 }}>
            <ArrowBackRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <InlineEdit
                value={playbook.name}
                onSave={(name) => saveField({ name })}
                ariaLabel="agent name"
                maxLength={120}
                editInputSx={{ fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.02em' }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ fontSize: '1.2rem', letterSpacing: '-0.02em', minWidth: 0 }}
                  noWrap
                >
                  {playbook.name}
                </Typography>
              </InlineEdit>
            </Box>
            {isArchived && (
              <Chip label="Archived" size="small" sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
            )}
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ArticleRounded sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {playbook.has_output_template ? 'Word + Excel' : 'Word'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {playbook.visibility === 'private'
                ? <LockRounded sx={{ fontSize: 13, color: 'text.secondary' }} />
                : <GroupRounded sx={{ fontSize: 13, color: 'text.secondary' }} />}
              <Typography variant="caption" color="text.secondary">
                {playbook.visibility === 'private' ? 'Personal' : 'Team'}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Created {formatRelativeTime(playbook.created_at)}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Button
            variant="outlined"
            size="small"
            endIcon={<OpenInNewRounded sx={{ fontSize: 14 }} />}
            onClick={() => navigate(`/copilot/${playbook.source_thread_id}`)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            View source chat
          </Button>
          <Tooltip title={isArchived ? 'Restore this agent to run it' : ''} disableHoverListener={!isArchived}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={isRunning ? <CircularProgress size={13} color="inherit" /> : <PlayArrowRounded sx={{ fontSize: 16 }} />}
                disabled={isRunning || isArchived}
                onClick={handleRun}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {isRunning ? 'Starting…' : 'Run now'}
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="More actions">
            <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)}>
              <MoreVertRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 180 } }}
          >
            {playbook.has_manual_run && (
              <MenuItem onClick={openManualRunDialog}>
                <BoltRounded fontSize="small" sx={{ mr: 1.5 }} />
                Run Manually
              </MenuItem>
            )}
            <MenuItem onClick={handleArchiveToggle}>
              {playbook.status === 'active'
                ? <Inventory2Rounded fontSize="small" sx={{ mr: 1.5 }} />
                : <UnarchiveRounded fontSize="small" sx={{ mr: 1.5 }} />}
              {playbook.status === 'active' ? 'Archive' : 'Restore'}
            </MenuItem>
            <Divider />
            <MenuItem
              sx={{ color: 'error.main' }}
              onClick={() => { setMoreAnchor(null); setDeleteConfirmOpen(true); }}
            >
              <DeleteRounded fontSize="small" sx={{ mr: 1.5 }} />
              Delete
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* Goal (editable in place) */}
      <Box
        sx={(t) => ({
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
          bgcolor: t.tokens?.surface?.sunken ?? t.palette.action.hover,
        })}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.6875rem', display: 'block', mb: 0.75 }}>
          Goal
        </Typography>
        <InlineEdit
          value={playbook.goal}
          onSave={(goal) => saveField({ goal })}
          ariaLabel="agent goal"
          multiline
          maxLength={2000}
        >
          <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
            {playbook.goal}
          </Typography>
        </InlineEdit>
      </Box>

      {/* What this agent does (methodology brief) */}
      <AgentBriefCard brief={playbook.methodology_brief} />

      {/* Output: generated document vs filled template */}
      <OutputCard playbook={playbook} onChange={setPlaybook} />

      <Divider sx={{ mb: 3 }} />

      {/* Schedules: recurring runs + email delivery */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Schedules
      </Typography>
      <Box sx={{ mb: 3 }}>
        <SchedulesManager
          playbookId={playbook.playbook_id}
          requirePeriodWindow={Boolean(playbook.has_manual_run)}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Run history */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Run history
      </Typography>

      {loadingRuns && <CircularProgress size={20} sx={{ display: 'block', my: 2 }} />}

      {!loadingRuns && runs.length === 0 && runsTotal === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No runs yet. Click <strong>Run now</strong> above to execute this agent.
        </Typography>
      )}

      {!loadingRuns && (runs.length > 0 || runsTotal > 0) && (
        <Box
          sx={(t) => ({
            border: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
          })}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={(t) => ({ bgcolor: t.tokens?.surface?.sunken ?? t.palette.action.hover })}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Started</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Completed</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => {
                const outputs = run.outputs || [];
                const canDownload = run.status === 'completed' && outputs.length > 0 && Boolean(playbookId);
                return (
                  <TableRow key={run.run_id} hover>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      {formatRelativeTime(run.started_at)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <RunStatusChip status={run.status} />
                        {run.status === 'failed' && run.error_message && (
                          <Tooltip title={run.error_message}>
                            <ErrorOutlineRounded sx={{ fontSize: 15, color: 'error.main', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                      {run.completed_at ? formatRelativeTime(run.completed_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        {run.thread_id && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ChatOutlinedRounded sx={{ fontSize: 15 }} />}
                            onClick={() => navigate(`/copilot/${run.thread_id}`)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, whiteSpace: 'nowrap' }}
                          >
                            Open Agent Chat
                          </Button>
                        )}
                        {canDownload && outputs.map((out) => {
                          const isDownloadingThis = downloadingRunId === `${run.run_id}:${out.artifact_id}`;
                          // Only label by type when there's more than one output.
                          const label = outputs.length > 1
                            ? (out.format === 'xlsx' ? 'Excel' : out.format === 'pptx' ? 'PPT' : 'Word')
                            : 'Download';
                          return (
                            <Button
                              key={out.artifact_id}
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={isDownloadingThis ? <CircularProgress size={13} color="inherit" /> : <DownloadRounded sx={{ fontSize: 15 }} />}
                              onClick={() => handleDownloadRun(run, out)}
                              disabled={Boolean(downloadingRunId)}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, whiteSpace: 'nowrap' }}
                            >
                              {label}
                            </Button>
                          );
                        })}
                        {canDownload && (
                          <Tooltip title="Email report to schedule recipients">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={
                                  sendingEmailRunId === run.run_id
                                    ? <CircularProgress size={13} color="inherit" />
                                    : <EmailRounded sx={{ fontSize: 15 }} />
                                }
                                onClick={() => handleSendEmail(run)}
                                disabled={Boolean(sendingEmailRunId)}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, whiteSpace: 'nowrap' }}
                              >
                                Send Email
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {!run.thread_id && !canDownload && (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={runsTotal}
            page={runsPage}
            onPageChange={(_, newPage) => setRunsPage(newPage)}
            rowsPerPage={runsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setRunsRowsPerPage(parseInt(e.target.value, 10));
              setRunsPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: (t) => `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
              '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 },
            }}
          />
        </Box>
      )}

      {/* Modals */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete agent?"
        description={`"${playbook.name}" and all its run history will be permanently deleted. This cannot be undone.`}
        tone="destructive"
        confirmLabel="Delete"
        onConfirm={handleDelete}
        busy={deleting}
      />

      <Dialog open={manualRunOpen} onClose={() => (manualRunSubmitting ? null : setManualRunOpen(false))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Run Manually</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.8125rem', mb: 2 }}>
            Runs the hardcoded report pipeline directly for the period below, bypassing the
            autonomous agent. Only the reporting period is needed — everything else (prior-week/
            month comparisons, connector) is derived automatically.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label="Period start"
              type="date"
              size="small"
              fullWidth
              value={manualRunPeriodStart}
              onChange={(e) => setManualRunPeriodStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={manualRunSubmitting}
            />
            <TextField
              label="Period end"
              type="date"
              size="small"
              fullWidth
              value={manualRunPeriodEnd}
              onChange={(e) => setManualRunPeriodEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={manualRunSubmitting}
            />
            {manualRunError && <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>{manualRunError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManualRunOpen(false)} disabled={manualRunSubmitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleManualRunSubmit}
            disabled={manualRunSubmitting}
            startIcon={manualRunSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {manualRunSubmitting ? 'Starting…' : 'Run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
